import React from "react";

import * as Styled from "./section-sidebar-nav.styled";

export interface SectionSidebarNavItem {
  id: string;
  label: string;
}

interface SectionSidebarNavProps {
  sections: SectionSidebarNavItem[];
  ariaLabel?: string;
  topOffset?: number;
  scrollContainerRef?: { current: HTMLElement | null };
}

export const SectionSidebarNav = ({
  sections,
  ariaLabel = "Section navigation",
  topOffset = 72,
  scrollContainerRef,
}: SectionSidebarNavProps) => {
  const [activeSectionId, setActiveSectionId] = React.useState(
    sections[0]?.id ?? "",
  );

  React.useEffect(() => {
    setActiveSectionId((prevSectionId) => {
      if (sections.length === 0) {
        return "";
      }

      const isCurrentSectionPresent = sections.some(
        (section) => section.id === prevSectionId,
      );

      return isCurrentSectionPresent ? prevSectionId : sections[0].id;
    });
  }, [sections]);

  const updateActiveSection = React.useCallback(() => {
    if (sections.length === 0) {
      return;
    }

    const contentScroller = scrollContainerRef?.current ?? null;
    let isScrolledToBottom = false;

    if (contentScroller) {
      isScrolledToBottom =
        contentScroller.scrollTop + contentScroller.clientHeight >=
        contentScroller.scrollHeight - 4;
    } else {
      isScrolledToBottom =
        window.scrollY + window.innerHeight >=
        document.documentElement.scrollHeight - 4;
    }

    if (isScrolledToBottom) {
      const lastSectionId = sections[sections.length - 1]?.id;

      if (lastSectionId) {
        setActiveSectionId((prevSectionId) =>
          prevSectionId === lastSectionId ? prevSectionId : lastSectionId,
        );
      }

      return;
    }

    const containerTop = contentScroller
      ? contentScroller.getBoundingClientRect().top
      : 0;
    let nextActiveSectionId = sections[0].id;

    sections.forEach((section) => {
      const targetSection = document.getElementById(section.id);

      if (!targetSection) {
        return;
      }

      if (targetSection.getBoundingClientRect().top - containerTop <= topOffset) {
        nextActiveSectionId = section.id;
      }
    });

    setActiveSectionId((prevSectionId) =>
      prevSectionId === nextActiveSectionId
        ? prevSectionId
        : nextActiveSectionId,
    );
  }, [scrollContainerRef, sections, topOffset]);

  React.useEffect(() => {
    updateActiveSection();
  }, [updateActiveSection]);

  React.useEffect(() => {
    const contentScroller = scrollContainerRef?.current ?? null;

    if (contentScroller) {
      contentScroller.addEventListener("scroll", updateActiveSection, {
        passive: true,
      });

      window.addEventListener("resize", updateActiveSection);

      return () => {
        contentScroller.removeEventListener("scroll", updateActiveSection);
        window.removeEventListener("resize", updateActiveSection);
      };
    }

    window.addEventListener("scroll", updateActiveSection, { passive: true });
    window.addEventListener("resize", updateActiveSection);

    return () => {
      window.removeEventListener("scroll", updateActiveSection);
      window.removeEventListener("resize", updateActiveSection);
    };
  }, [scrollContainerRef, updateActiveSection]);

  const handleSectionNavigation = React.useCallback(
    (sectionId: string) => {
      setActiveSectionId(sectionId);

      const targetSection = document.getElementById(sectionId);

      if (!targetSection) {
        return;
      }

      const contentScroller = scrollContainerRef?.current ?? null;

      if (!contentScroller) {
        targetSection.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
        return;
      }

      const containerTop = contentScroller.getBoundingClientRect().top;
      const targetTop = targetSection.getBoundingClientRect().top;

      contentScroller.scrollTo({
        top: contentScroller.scrollTop + targetTop - containerTop,
        behavior: "smooth",
      });
    },
    [scrollContainerRef],
  );

  return (
    <Styled.Sidebar aria-label={ariaLabel}>
      {sections.map((section) => (
        <Styled.SidebarButton
          key={section.id}
          type="button"
          $active={activeSectionId === section.id}
          onClick={() => handleSectionNavigation(section.id)}
          aria-current={activeSectionId === section.id ? "location" : undefined}
        >
          {section.label}
        </Styled.SidebarButton>
      ))}
    </Styled.Sidebar>
  );
};
